import React, { useEffect, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';

import { convertToTimezoneWithStartOfDayFormat } from 'utils/utility_functions/timezone';
import { fixToTwoDecimal, formatNumbersToCurrencyString } from 'utils/utils';
import {
  actionBody,
  billableTimeEntriesBody,
  caseNameBody,
  dateTemplate,
  durationTemplate,
  teamMemberBody,
  teamMemberEditTimeEntries,
  timeEntryDataTableFooterKeyTemplate,
  timeEntryDataTableFooterValueTemplate,
  totalBody,
  totalHoursTemplate,
} from './helpers/timeEntriesDataTableHelpers';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { Tooltip } from 'primereact/tooltip';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

const TimeEntries = (props) => {
  const {
    activityTypes,
    caseDetails,
    timeEntires: initialTimeEntries,
    userContext,
    addTimeEntry,
    timeEntiresError,
    setTimeEntries,
    setTimeEntriesError,
    isChanged,
    setIsChanged,
    deleteTimeEntry,
    isEverGreenRetainer,
    isStandardRetainer,
    teamMembers,
    totalHours,
    timeEntriesTotal,
    rateWithTax,
    selectedCase,
    createTimeEntries,
    updateTimeEntries,
    allTimeEntriesSaved,
    setAllTimeEntriesSaved,
    createTimeEntryInvoiceClicked,
    setCreateTimeEntryInvoiceClicked,
    setTimeEntriesTotal,
    setTotalHours,
  } = props || {};

  const { addToast } = useToast();
  const [modifiedRows, setModifiedRows] = useState({});
  const [editingRows, setEditingRows] = useState({});

  const onChangeTeamMemberEditTimeEntries = (e, index) => {
    const updatedRows = { ...editingRows };
    const rowData = { ...(updatedRows[index] || initialTimeEntries[index]) };

    let name = e.target?.value?.name;
    let contact_id = e.target?.value?.contact_id;
    let team_member = caseDetails?.team_member?.filter((index) => index.contact_id === contact_id);

    if (team_member && team_member.length > 0) {
      rowData.hour_rate = team_member[0]?.rate;
      rowData.team_member_firm_user_id = team_member[0]?.firm_user_id;
      rowData.team_member_cognito_id = team_member[0]?.client_cognito_username;
    }

    rowData.team_member_name = name;
    rowData.team_member_id = contact_id;

    updatedRows[index] = rowData;
    setEditingRows(updatedRows);

    timeEntiresError[`${index}_team_member`] = '';
    setTimeEntriesError({ ...timeEntiresError });
    isChanged.time_entries = true;
    setIsChanged({ ...isChanged });
    markRowAsModified(index);
  };

  const onChangeBillable = async (e, index) => {
    const checked = e?.target?.checked;
    const updatedRows = { ...editingRows };
    const rowData = { ...(updatedRows[index] || initialTimeEntries[index]) };

    rowData.billable = checked;
    updatedRows[index] = rowData;
    setEditingRows(updatedRows);

    const updatedTimeEntries = [...initialTimeEntries];
    updatedTimeEntries[index] = rowData;
    setTimeEntries(updatedTimeEntries); // Triggers total recalculation

    // 3. Check if ONLY billable was changed (no other edits)
    const isOnlyBillableChange = !modifiedRows[index] && Object.keys(editingRows[index] || {}).length <= 1;

    isChanged.time_entries = true;
    setIsChanged({ ...isChanged });

    if (isOnlyBillableChange) {
      try {
        delete rowData.added_new_time_entry;

        const updatedEditingRows = { ...editingRows };
        delete updatedEditingRows[index];
        setEditingRows(updatedEditingRows);

        markRowAsUnmodified(index);
        setAllTimeEntriesSaved(true);
      } catch (err) {
        console.error(err);

        rowData.billable = !checked;
        updatedRows[index] = rowData;
        setEditingRows(updatedRows);
        updatedTimeEntries[index] = rowData;
        setTimeEntries(updatedTimeEntries);
      }
    } else {
      markRowAsModified(index);
    }
  };

  useEffect(() => {
    const combinedTimeEntries = initialTimeEntries.map((entry, idx) => {
      return editingRows[idx] ? editingRows[idx] : entry;
    });

    let subTotal = 0;
    let hours = 0;

    combinedTimeEntries.forEach((entry) => {
      if (entry.billable) {
        let hour_rate = parseFloat(entry.hour_rate) || 0;
        let total_hours = parseFloat(entry.total_hours) || 0;
        subTotal += hour_rate * total_hours;
        hours += total_hours;
      }
    });

    setTimeEntriesTotal(fixToTwoDecimal(subTotal));
    setTotalHours(hours.toFixed(2));
  }, [initialTimeEntries, editingRows]);

  const billingCodeTemplate = (item, props) => {
    if (item) {
      return (
        <div className="d-flex align-items-center">
          {item?.activity_type_code}-{item?.activity_type}
        </div>
      );
    }
    return <span>{props.placeholder}</span>;
  };

  const markRowAsModified = (rowIndex) => {
    setModifiedRows((prev) => {
      const updated = { ...prev, [rowIndex]: true };
      setAllTimeEntriesSaved(false);
      return updated;
    });

    setIsChanged((prev) => ({ ...prev, time_entries: true }));
  };

  const markRowAsUnmodified = (rowIndex) => {
    setModifiedRows((prev) => {
      const updated = { ...prev };
      delete updated[rowIndex];

      const hasOtherChanges = Object.keys(updated).length > 0;

      setAllTimeEntriesSaved(!hasOtherChanges);
      setIsChanged((prev) => ({ ...prev, time_entries: hasOtherChanges }));

      return updated;
    });
  };

  const handleRowDeletion = (rowIndex) => {
    setModifiedRows((prev) => {
      const updated = { ...prev };
      delete updated[rowIndex];
      setAllTimeEntriesSaved(Object.keys(updated).length === 0);
      return updated;
    });

    setEditingRows((prev) => {
      const updated = { ...prev };
      delete updated[rowIndex];
      return updated;
    });

    setIsChanged((prev) => ({
      ...prev,
      time_entries: Object.keys(modifiedRows).filter((key) => key !== rowIndex.toString()).length > 0,
    }));
  };

  const handleSaveRow = async (rowIndex) => {
    const rowData = editingRows[rowIndex] || initialTimeEntries[rowIndex];
    const errors = {};

    if (!rowData.time_entry_date) {
      errors[`${rowIndex}_date`] = 'Required';
    }
    if (!rowData.team_member_id) {
      errors[`${rowIndex}_team_member`] = 'Required';
    }
    if (!rowData.activity_type_id) {
      errors[`${rowIndex}_activity_type`] = 'Required';
    }
    if (!rowData.hour_rate || parseFloat(rowData.hour_rate) <= 0) {
      errors[`${rowIndex}_hour_rate`] = 'Required';
    }
    if (!rowData.total_hours || parseFloat(rowData.total_hours) <= 0) {
      errors[`${rowIndex}_total_hours`] = 'Required';
    }

    if (Object.keys(errors).length > 0) {
      setTimeEntriesError((prev) => ({ ...prev, ...errors }));
      return;
    }

    const onTimeEntrySuccess = () => {
      delete rowData?.added_new_time_entry;

      const updatedTimeEntries = [...initialTimeEntries];
      updatedTimeEntries[rowIndex] = rowData;
      setTimeEntries(updatedTimeEntries);

      const updatedEditingRows = { ...editingRows };
      delete updatedEditingRows[rowIndex];
      setEditingRows(updatedEditingRows);

      markRowAsUnmodified(rowIndex);
    };

    try {
      if (rowData.added_new_time_entry && !rowData?.pk) {
        await createTimeEntries(rowData, onTimeEntrySuccess);
      } else {
        await updateTimeEntries(rowData, onTimeEntrySuccess);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getRowData = (rowIndex) => {
    return editingRows[rowIndex] || initialTimeEntries[rowIndex];
  };

  return (
    <>
      <div className="datatable-responsive">
        <DataTable
          className="p-datatable-responsive"
          value={initialTimeEntries}
          editMode="cell"
          emptyMessage={() => <div onClick={addTimeEntry}>No records found</div>}
        >
          <Column
            header="Date"
            field="time_entry_date"
            body={(rowData, props) => {
              return dateTemplate({ rowData: getRowData(props.rowIndex), props, timeEntiresError });
            }}
            editor={(props) => {
              return (
                <div className="table-input">
                  <Calendar
                    value={getRowData(props.rowIndex).time_entry_date}
                    onChange={(e) => {
                      let index = props.rowIndex;
                      let date = e.value;
                      const updatedRows = { ...editingRows };
                      const rowData = { ...(updatedRows[index] || initialTimeEntries[index]) };

                      rowData.time_entry_date = convertToTimezoneWithStartOfDayFormat(date, userContext?.userDetails.timezone);
                      updatedRows[index] = rowData;
                      setEditingRows(updatedRows);

                      timeEntiresError[`${index}_date`] = '';
                      setTimeEntriesError({ ...timeEntiresError });
                      isChanged.time_entries = true;
                      setIsChanged({ ...isChanged });
                      markRowAsModified(index);
                    }}
                  />
                </div>
              );
            }}
            footer={timeEntryDataTableFooterKeyTemplate({
              isEvergreenOrStandardRetainer: isEverGreenRetainer || isStandardRetainer,
              taxPercentage: caseDetails?.billing_preferences?.tax_percentage,
            })}
            sortable
          />
          <Column
            header="Case Name"
            body={(rowData, props) =>
              caseNameBody({
                caseName: getRowData(props.rowIndex)?.case_name || caseDetails?.case_description?.case_name || selectedCase?.case_name,
              })
            }
          />
          <Column header="Activity" body={(rowData, props) => actionBody({ rowData: getRowData(props.rowIndex), props })} />
          <Column
            header="Members"
            body={(rowData, props) => {
              return teamMemberBody({ rowData: getRowData(props.rowIndex), props, timeEntiresError });
            }}
            sortable
            editor={(props) => {
              return teamMemberEditTimeEntries({
                props,
                teamMembers,
                timeEntiresError,
                timeEntires: initialTimeEntries,
                onChangeTeamMemberEditTimeEntries,
                editingRows,
              });
            }}
          />
          <Column
            header="Billing Code"
            className="time-entry-billing"
            body={(rowData, props) => {
              const rowEntry = getRowData(props.rowIndex);
              return (
                <>
                  <span className="p-column-title text-break">Billing Code</span>
                  <span className="text-break">
                    {rowEntry.activity_type ? (
                      `${rowEntry?.activity_type_code}-${rowEntry?.activity_type}`
                    ) : props?.rowEntry?.module_name ? (
                      '-'
                    ) : (
                      <i>Please Enter</i>
                    )}
                  </span>
                  <br></br>
                  {timeEntiresError && timeEntiresError[`${props.rowIndex}_activity_type`] && (
                    <span className="text-danger">{timeEntiresError[`${props.rowIndex}_activity_type`]}</span>
                  )}
                </>
              );
            }}
            sortable
            editor={(props) => {
              const rowData = getRowData(props.rowIndex);
              return (
                <div>
                  <Dropdown
                    options={activityTypes}
                    value={{
                      activity_type_code: rowData?.activity_type_code,
                      activity_type: rowData?.activity_type,
                      activity_type_id: rowData?.activity_type_id,
                    }}
                    optionLabel={'activity_type'}
                    itemTemplate={billingCodeTemplate}
                    valueTemplate={billingCodeTemplate}
                    template
                    onChange={(e) => {
                      let activity_type = e.target.value?.activity_type || '';
                      let activity_type_id = e.target.value?.activity_type_id || '';
                      let activity_type_code = e.target.value?.activity_type_code || '';
                      let index = props.rowIndex;

                      const updatedRows = { ...editingRows };
                      const rowData = { ...(updatedRows[index] || initialTimeEntries[index]) };

                      rowData.activity_type = activity_type;
                      rowData.activity_type_id = activity_type_id;
                      rowData.activity_type_code = activity_type_code;

                      updatedRows[index] = rowData;
                      setEditingRows(updatedRows);

                      timeEntiresError[`${props.rowIndex}_activity_type`] = '';
                      setTimeEntriesError({ ...timeEntiresError });
                      isChanged.time_entries = true;
                      setIsChanged({ ...isChanged });
                      markRowAsModified(index);
                    }}
                    className="w-100"
                    placeholder="Select"
                    filter
                    showFilterClear={true}
                    showClear={rowData.activity_type_id && rowData.activity_type ? true : false}
                  />
                  {timeEntiresError && timeEntiresError[`${props?.rowIndex}_activity_type`] && (
                    <span className="text-danger">{timeEntiresError[`${props.rowIndex}_activity_type`]}</span>
                  )}
                </div>
              );
            }}
          />
          <Column
            header="Description"
            field="notes"
            body={(rowData, props) => {
              const rowEntry = getRowData(props.rowIndex);
              return (
                <>
                  <span className="p-column-title text-break">Description</span>
                  <span className="text-break">{rowEntry.notes ? rowEntry.notes : <i>Please Enter</i>}</span>
                </>
              );
            }}
            editor={(props) => {
              const rowData = getRowData(props.rowIndex);
              return (
                <TextSnippetWrapper
                  type="text"
                  value={rowData?.notes}
                  onChange={(e) => {
                    let index = props.rowIndex;
                    const updatedRows = { ...editingRows };
                    const rowData = { ...(updatedRows[index] || initialTimeEntries[index]) };

                    rowData.notes = e.target.value;
                    updatedRows[index] = rowData;
                    setEditingRows(updatedRows);

                    isChanged.time_entries = true;
                    setIsChanged({ ...isChanged });
                    markRowAsModified(index);
                  }}
                  className="w-100 input-shadow"
                />
              );
            }}
          />
          <Column
            header="Rate"
            field="hour_rate"
            sortable
            body={(rowData, props) => {
              const rowEntry = getRowData(props.rowIndex);
              return (
                <>
                  <span className="p-column-title text-break">Rate</span>
                  <span className="text-break">
                    {rowEntry.hour_rate ? `${formatNumbersToCurrencyString(rowEntry.hour_rate)}` : <i>Please Enter</i>}
                  </span>
                  <br></br>
                  {timeEntiresError && timeEntiresError[`${props.rowIndex}_hour_rate`] && (
                    <span className="text-danger">{timeEntiresError[`${props.rowIndex}_hour_rate`]}</span>
                  )}
                </>
              );
            }}
            editor={(props) => {
              const rowData = getRowData(props.rowIndex);
              return (
                <div>
                  <InputText
                    value={rowData?.hour_rate}
                    onChange={(e) => {
                      let index = props.rowIndex;
                      const updatedRows = { ...editingRows };
                      const rowData = { ...(updatedRows[index] || initialTimeEntries[index]) };

                      rowData.hour_rate = fixToTwoDecimal(e.target.value);
                      updatedRows[index] = rowData;
                      setEditingRows(updatedRows);

                      timeEntiresError[`${props.rowIndex}_hour_rate`] = '';
                      setTimeEntriesError({ ...timeEntiresError });
                      isChanged.time_entries = true;
                      setIsChanged({ ...isChanged });
                      markRowAsModified(index);
                    }}
                    className="w-100 input-shadow"
                    type="number"
                    min={0}
                    keyfilter={'pnum'}
                  />
                  {timeEntiresError && timeEntiresError[`${props.rowIndex}_hour_rate`] && (
                    <span className="text-danger">{timeEntiresError[`${props.rowIndex}_hour_rate`]}</span>
                  )}
                </div>
              );
            }}
          />
          <Column
            header="Duration"
            field="total_hours"
            body={(rowData, props) => {
              return durationTemplate({ rowData: getRowData(props.rowIndex), props, timeEntiresError });
            }}
            editor={(props) => {
              const rowData = getRowData(props.rowIndex);
              return (
                <div>
                  <InputText
                    value={rowData?.total_hours}
                    onChange={(e) => {
                      let index = props.rowIndex;
                      const updatedRows = { ...editingRows };
                      const rowData = { ...(updatedRows[index] || initialTimeEntries[index]) };

                      rowData.total_hours = fixToTwoDecimal(e.target.value);
                      updatedRows[index] = rowData;
                      setEditingRows(updatedRows);

                      timeEntiresError[`${props.rowIndex}_total_hours`] = '';
                      setTimeEntriesError({ ...timeEntiresError });
                      isChanged.time_entries = true;
                      setIsChanged({ ...isChanged });
                      markRowAsModified(index);
                    }}
                    className="w-100 input-shadow"
                    type="number"
                    min={0}
                    keyfilter={'pnum'}
                  />
                  <small id="username1-help" className="p-d-block">
                    0.1 = 6 minutes
                  </small>
                  {timeEntiresError && timeEntiresError[`${props.rowIndex}_total_hours`] && (
                    <span className="text-danger">{timeEntiresError[`${props.rowIndex}_total_hours`]}</span>
                  )}
                </div>
              );
            }}
            footer={() => totalHoursTemplate(totalHours)}
            sortable
          />
          <Column
            header="Total"
            body={(rowData, props) => totalBody(getRowData(props.rowIndex))}
            footer={timeEntryDataTableFooterValueTemplate({
              isEvergreenOrStandardRetainer: isEverGreenRetainer || isStandardRetainer,
              taxPercentage: caseDetails?.billing_preferences?.tax_percentage,
              timeEntriesTotal,
              rateWithTax,
            })}
          />
          <Column
            header="Billable"
            className="time-entry-billable"
            body={(rowData, props) => {
              return billableTimeEntriesBody({ rowData: getRowData(props.rowIndex), props, onChangeBillable });
            }}
          />
          <Column
            className="time-entry-delete time-entry-width"
            body={(rowData, props) => {
              return (
                <div className="d-flex flex-column align-items-start">
                  <span className="p-column-title text-break">Actions</span>
                  <div className="d-flex align-items-center">
                    {modifiedRows[props?.rowIndex] && (
                      <>
                        <i
                          className={`bi bi-check2-circle cursor-pointer save-icon-row-${props?.rowIndex}`}
                          style={{
                            marginRight: '10px',
                            fontSize: '13px',
                            marginTop: '1px',
                          }}
                          onClick={() => handleSaveRow(props?.rowIndex)}
                        />
                        <Tooltip
                          target={`.save-icon-row-${props?.rowIndex}`}
                          content="Save to add amount to total"
                          position="top"
                          showDelay={500}
                        />
                      </>
                    )}

                    <i
                      className="icon-delete icon-red fmt-2"
                      onClick={(e) => {
                        deleteTimeEntry(props);
                        handleRowDeletion(props?.rowIndex);
                      }}
                    />
                  </div>
                  {createTimeEntryInvoiceClicked && modifiedRows[props.rowIndex] && <span className="text-danger">Please Save</span>}
                </div>
              );
            }}
          />
        </DataTable>
        <div className="col-12 p-2">
          <span className="mt-3 addfield" role="button" onClick={addTimeEntry}>
            {' '}
            <i className="icon-add F-size14 me-1"></i>Add Time Entry
          </span>
        </div>
      </div>
    </>
  );
};

export default TimeEntries;
